// components/calculator/CompanyInfoSection.js
import React, { useState, useEffect } from 'react';
import { 
  Typography, 
  Grid, 
  TextField, 
  Autocomplete, 
  Box,
  Chip,
  Alert,
  CircularProgress,
  Paper,
  Divider
} from '@mui/material';
import { Business, AccessTime, AttachMoney, Search, Info, CheckCircle, Park, MedicalServices, Home } from '@mui/icons-material';
import { axiosInstance } from '../../utils/axios';
import { TIPO_OPTIONS } from '../clients/parametros';

const IVA_RATE = 0.21;
const BONO_BASE_RATE = 937.5;

// Tipos de cliente disponibles (para mostrar iconos y descripciones)
const TIPOS_CLIENTE = TIPO_OPTIONS

// Función para obtener datos del tipo por número
const getTipoClienteData = (tipoNumerico) => {
  return TIPOS_CLIENTE.find(t => t.value === parseInt(tipoNumerico)) || null;
};

const CompanyInfoSection = ({ formData, setFormData }) => {
  const [clients, setClients] = useState([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientError, setClientError] = useState('');

  // Cargar clientes al montar el componente
  useEffect(() => {
    fetchClients();
  }, []);

  // Sincronizar cliente seleccionado cuando se carga un cálculo existente
  useEffect(() => {
    if (formData.clienteId && clients.length > 0) {
      const client = clients.find(c => c.id === formData.clienteId);
      if (client) {
        setSelectedClient(client);
      }
    }
  }, [formData.clienteId, clients]);

  // Función para obtener la lista de clientes
  const fetchClients = async () => {
    setLoadingClients(true);
    setClientError('');
    try {
      const response = await axiosInstance.get('/clientes');
      setClients(response.data);
    } catch (error) {
      console.error('Error al cargar clientes:', error);
      setClientError('Error al cargar clientes');
      
      // Datos de ejemplo en caso de error
      setClients([
        {
          id: 1,
          razon_social: 'Empresa Demo S.A.',
          horas: 160,
          monto_base: 150000,
          iva: 0,
          tipo: 3
        },
        {
          id: 2,
          razon_social: 'Tecnología ABC',
          horas: 80,
          monto_base: 85000,
          iva: 1,
          tipo: 2
        },
        {
          id: 3,
          razon_social: 'Servicios XYZ Ltda.',
          horas: 120,
          monto_base: 95000,
          iva: 0,
          tipo: 1
        }
      ]);
    } finally {
      setLoadingClients(false);
    }
  };

  // Función para convertir monto con IVA a sin IVA
  const convertirMontoSinIva = (monto, tieneIva) => {
    if (!tieneIva || tieneIva === 0) {
      return monto; // Ya está sin IVA
    }
    return monto / (1 + IVA_RATE); // Quitar el IVA
  };

  // Función para calcular el bono navideño
  const calcularBonoNavideno = (horas) => {
    return BONO_BASE_RATE * (horas || 0);
  };

  // Función para manejar la selección de cliente
  const handleClientSelect = (event, newValue) => {
    setSelectedClient(newValue);
    
    if (newValue) {
      // Convertir monto a sin IVA si es necesario
      const montoSinIva = convertirMontoSinIva(newValue.monto_base, newValue.iva);
      
      // Mantener tipo de cliente como número
      const tipoClienteNumerico = parseInt(newValue.tipo) || 0;
      
      console.log('=== CLIENTE SELECCIONADO ===');
      console.log('Cliente:', newValue.razon_social);
      console.log('Tipo desde DB:', newValue.tipo);
      console.log('Tipo convertido:', tipoClienteNumerico);
      
      // Cargar datos del cliente seleccionado
      setFormData(prev => ({
        ...prev,
        nombreEmpresa: newValue.razon_social,
        horasServicio: newValue.horas.toString(),
        montoBase: montoSinIva.toString(),
        clienteId: newValue.id,
        tipoCliente: tipoClienteNumerico,
        clienteData: {
          nombre: newValue.razon_social,
          horas: newValue.horas,
          monto: newValue.monto_base,
          iva: newValue.iva || 0,
          tipoCliente: tipoClienteNumerico
        }
      }));
    } else {
      // Limpiar datos si no hay cliente seleccionado
      setFormData(prev => ({
        ...prev,
        nombreEmpresa: '',
        horasServicio: '',
        montoBase: '',
        clienteId: null,
        tipoCliente: 0,
        clienteData: {
          nombre: '',
          horas: 0,
          monto: 0,
          iva: 0,
          tipoCliente: 0
        }
      }));
    }
  };

  // Función para formatear la opción en el autocomplete
  const formatClientOption = (option) => {
    if (!option) return '';
    const montoSinIva = convertirMontoSinIva(option.monto_base, option.iva);
    const ivaLabel = option.iva ? ' (Con IVA)' : ' (Sin IVA)';
    return `${option.razon_social} - ${option.horas}hs - $${option.monto_base?.toLocaleString('es-AR')}${ivaLabel}`;
  };

  return (
    <Paper elevation={2} sx={{ p: 3, mt: 3 }}>
      <Typography variant="h6" sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Business />
        Seleccionar Cliente
      </Typography>

      {/* Selector de Cliente */}
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Autocomplete
            value={selectedClient}
            onChange={handleClientSelect}
            options={clients}
            getOptionLabel={(option) => option.razon_social || ''}
            loading={loadingClients}
            loadingText="Cargando clientes..."
            noOptionsText="No se encontraron clientes"
            renderInput={(params) => (
              <TextField
                {...params}
                label="Buscar y Seleccionar Cliente"
                placeholder="Escriba para buscar cliente por razón social..."
                InputProps={{
                  ...params.InputProps,
                  startAdornment: <Search sx={{ mr: 1, color: 'action.active' }} />,
                  endAdornment: (
                    <>
                      {loadingClients ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            renderOption={(props, option) => (
              <Box component="li" {...props}>
                <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                  <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                    <Business sx={{ mr: 1, fontSize: 16, verticalAlign: 'middle' }} />
                    {option.razon_social}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, mt: 0.5, alignItems: 'center' }}>
                    <Chip 
                      size="small" 
                      icon={<AccessTime />}
                      label={`${option.horas} hs`} 
                      color="info" 
                      variant="outlined" 
                    />
                    <Chip 
                      size="small" 
                      icon={<AttachMoney />}
                      label={`$${option.monto_base?.toLocaleString('es-AR')}`} 
                      color="success" 
                      variant="outlined" 
                    />
                    <Chip 
                      size="small" 
                      label={option.iva ? 'CON IVA' : 'SIN IVA'} 
                      color={option.iva ? 'warning' : 'primary'} 
                      variant="outlined" 
                    />
                    <Chip 
                      size="small" 
                      icon={getTipoClienteData(option.tipo)?.icon}
                      label={getTipoClienteData(option.tipo)?.label || `Tipo ${option.tipo}`} 
                      color="secondary" 
                      variant="outlined" 
                    />
                    <Chip 
                      size="small" 
                      label={`Bono: $${calcularBonoNavideno(option.horas).toLocaleString('es-AR')}`} 
                      color="error" 
                      variant="outlined" 
                    />
                  </Box>
                </Box>
              </Box>
            )}
          />
          
          {clientError && (
            <Alert severity="warning" sx={{ mt: 1 }}>
              {clientError}. Mostrando datos de ejemplo.
            </Alert>
          )}
        </Grid>
      </Grid>

      {/* Instrucciones cuando no hay cliente seleccionado */}
      {!selectedClient && (
        <Alert severity="info" sx={{ mt: 2 }}>
          <Typography variant="body2">
            <Info sx={{ mr: 1, verticalAlign: 'middle', fontSize: 16 }} />
            <strong>Seleccione un cliente</strong> para cargar automáticamente sus datos y proceder con el cálculo de aumentos.
            Los datos del cliente seleccionado no podrán editarse y se usarán como base para los cálculos.
          </Typography>
        </Alert>
      )}
    </Paper>
  );
};

export default CompanyInfoSection;